import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { CompanyLogoAvatar } from "@/components/CompanyLogoAvatar/CompanyLogoAvatar";
import { classNames } from "@/utils/classNames";

import { ListPreviewResult, PreviewResultStatus } from "../schema/listPreview";

interface PreviewResultsTableProps {
	results: ListPreviewResult[];
}

const statusColors: Record<PreviewResultStatus, string> = {
	[PreviewResultStatus.FOUND]: "text-status-success bg-status-success/10",
	[PreviewResultStatus.UNSURE]: "text-status-warning bg-status-warning/10",
	[PreviewResultStatus.NOT_FOUND]: "text-status-error bg-status-error/10",
	[PreviewResultStatus.SKIPPED]: "text-text-meta bg-gray-100",
	[PreviewResultStatus.COMPANY_DISQUALIFIED]: "text-text-meta bg-gray-100",
	[PreviewResultStatus.COMPANY_NOT_FOUND]: "text-status-error bg-status-error/10",
};

export const PreviewResultsTable: FunctionComponent<PreviewResultsTableProps> = ({ results }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.preview.${key}`);

	if (results.length === 0) {
		return (
			<div className="mt-4 rounded-lg border border-border-light bg-brand-white p-8 text-center text-sm text-text-meta">
				{ts("noResults")}
			</div>
		);
	}

	return (
		<div className="mt-4 overflow-hidden rounded-lg border border-border-light">
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b border-border-light bg-brand-white">
						<th className="px-4 py-3 text-left font-medium text-text-meta">{ts("company")}</th>
						<th className="px-4 py-3 text-left font-medium text-text-meta">{ts("personFound")}</th>
						<th className="px-4 py-3 text-left font-medium text-text-meta">{ts("via")}</th>
						<th className="px-4 py-3 text-left font-medium text-text-meta">{ts("status")}</th>
					</tr>
				</thead>
				<tbody>
					{results.map((result) => (
						<tr key={result.id} className="border-b border-border-light last:border-b-0 bg-white">
							{/* Company */}
							<td className="px-4 py-3">
								<div className="flex items-center gap-2">
									<CompanyLogoAvatar
										logoUrl={result.organizationLogo}
										name={result.organizationName}
									/>
									<div>
										<div className="font-medium text-text">{result.organizationName}</div>
										{result.organizationSize && (
											<div className="text-xs text-text-meta">{result.organizationSize}</div>
										)}
									</div>
								</div>
							</td>

							{/* Person Found */}
							<td className="px-4 py-3">
								{result.personName ? (
									<div className="flex items-center gap-2">
										{result.personImage ? (
											<img
												src={result.personImage}
												alt={result.personName}
												className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
											/>
										) : (
											<div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-text-meta">
												{result.personAvatar ?? result.personName?.slice(0, 2)?.toUpperCase()}
											</div>
										)}
										<div>
											{result.linkedinUrl ? (
												<a
													href={result.linkedinUrl}
													target="_blank"
													rel="noopener noreferrer"
													className="font-medium text-blue-action hover:underline"
												>
													{result.personName}
												</a>
											) : (
												<div className="text-text">{result.personName}</div>
											)}
											{result.personTitle && (
												<div className="text-xs text-text-meta">{result.personTitle}</div>
											)}
										</div>
									</div>
								) : (
									<span className="text-text-meta">—</span>
								)}
							</td>

							{/* Via */}
							<td className="px-4 py-3">
								{result.matchedVia ? (
									<span className="text-xs text-text-meta">
										{ts(`matchedVia.${result.matchedVia}`)}
									</span>
								) : (
									<span className="text-text-meta">—</span>
								)}
							</td>

							{/* Status */}
							<td className="px-4 py-3">
								<span
									className={classNames(
										"inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
										result.status ? statusColors[result.status] : "text-text-meta bg-gray-100"
									)}
								>
									{result.status ? ts(result.status.toLowerCase()) : "Skipped"}
								</span>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};
